import axios from "axios";
import { API_BASE_URL } from "../../../constants";

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const unwrapError = (error) => {
  if (error.response) {
    const detail = error.response.data
      ? error.response.data.error || JSON.stringify(error.response.data)
      : error.response.statusText;
    throw new Error(`HTTP ${error.response.status}: ${detail}`);
  }
  throw error;
};

const get = async (path, params) => {
  try {
    const response = await axios.get(`${API_BASE_URL}${path}`, { headers: authHeaders(), params });
    return response.data;
  } catch (error) {
    return unwrapError(error);
  }
};

const send = async (method, path, body) => {
  try {
    const response = await axios({ method, url: `${API_BASE_URL}${path}`, data: body, headers: authHeaders() });
    return response.data;
  } catch (error) {
    return unwrapError(error);
  }
};

// ---------------------------------------------------------------- Discover
// Full ranked athlete pool. Filtering happens client-side for a snappy UI;
// the endpoint also accepts sport/gender/state/tier/dealType/minScore/search
// params if a server-side pass is ever needed.
export const fetchDiscoverPool = () => get("/brand/discover/");

// --------------------------------------------------------------- Shortlist
export const fetchShortlist = () => get("/brand/shortlist/");

// items: [{athleteId, deliverables, offer}]
export const saveShortlist = ({ items, targetBudget }) =>
  send("put", "/brand/shortlist/", { items, targetBudget });

// -------------------------------------------------------------------- Home
export const fetchBrandHome = () => get("/brand/home/");
export const fetchBrandBadges = () => get("/brand/messages/unread/");

// --------------------------------------------------------------- Campaigns
export const fetchCampaigns = (params) => get("/brand/campaigns/", params);
export const createCampaign = (payload) => send("post", "/brand/campaigns/", payload);
export const fetchCampaignDetail = (campaignId) => get(`/brand/campaigns/${campaignId}/`);
export const updateCampaign = (campaignId, payload) => send("put", `/brand/campaigns/${campaignId}/`, payload);
export const deleteCampaign = (campaignId) => send("delete", `/brand/campaigns/${campaignId}/`);
// action: archive | cancel | complete | reopen
export const campaignAction = (campaignId, action) => send("post", `/brand/campaigns/${campaignId}/${action}/`);

// ------------------------------------------------------------------ Offers
export const fetchComposeOffer = (campaignId, athleteId) =>
  get(`/brand/campaigns/${campaignId}/offer/${athleteId}/`);
// payload: {action: 'send' | 'draft', athleteIds?, ...offer fields}
export const submitOffer = (campaignId, athleteId, payload) =>
  send("post", `/brand/campaigns/${campaignId}/offer/${athleteId}/`, payload);

// ------------------------------------------------------------------- Deals
export const fetchDeal = (dealId, { fullPool = false } = {}) =>
  get(`/brand/deals/${dealId}/`, fullPool ? { pool: "full" } : undefined);
// action: followup | counter-accept | counter-decline | counter-reply |
//         contract-send | contract-sign | contract-upload | withdraw | complete
export const dealAction = (dealId, action, payload = {}) => send("post", `/brand/deals/${dealId}/${action}/`, payload);
export const submissionAction = (dealId, submissionId, action, payload = {}) =>
  send("post", `/brand/deals/${dealId}/submissions/${submissionId}/${action}/`, payload);
export const releaseMilestone = (dealId, milestoneId, payload = {}) =>
  send("post", `/brand/deals/${dealId}/milestones/${milestoneId}/release/`, payload);

// The contract download needs the bearer token, so fetch it as a blob and
// hand the browser an object URL instead of linking to the API directly.
export const downloadContract = async (dealId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/brand/deals/${dealId}/contract/download/`, {
      headers: authHeaders(),
      responseType: "blob",
    });
    const disposition = response.headers["content-disposition"] || "";
    const match = /filename="?([^"]+)"?/.exec(disposition);
    const fileName = match ? match[1] : "contract.txt";
    const url = window.URL.createObjectURL(response.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    return fileName;
  } catch (error) {
    return unwrapError(error);
  }
};

// ---------------------------------------------------------------- Messages
export const fetchThreads = (campaignId) => get("/brand/messages/", campaignId ? { campaignId } : undefined);
export const startThread = (campaignId, athleteId) => send("post", "/brand/messages/", { campaignId, athleteId });
export const fetchThread = (threadId) => get(`/brand/messages/${threadId}/`);
export const sendMessage = (threadId, payload) => send("post", `/brand/messages/${threadId}/`, payload);
